import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Slack as SlackIcon, Plus, Trash2, Save, Unplug } from "lucide-react";
import { BridgeApi } from "../lib/api";
import { useToast } from "../lib/toast";
import { PageBody, PageHeader } from "../components/PageScaffold";
import { Button, Pill, EmptyState } from "../components/primitives";
import { cn } from "../lib/cn";
import type { SlackMentionChannel, SlackMentionConnection } from "../lib/types";

/**
 * Slack @-mentions — the Alfred bot, Claude-Tag style.
 *
 * Tag @Alfred in an enabled channel and alfred answers in the thread, as the bot.
 * alfred consults other personas over the fleet bus when it needs them. Then it is
 * done: no route is created and nothing is left running.
 *
 * This is NOT the Slack CHANNEL (Channels page). That one reads as the operator's
 * own account, for monitoring only, and never posts.
 */
export default function Slack() {
  const qc = useQueryClient();
  const { data: conn } = useQuery({ queryKey: ["slack-mentions", "connection"], queryFn: BridgeApi.slackMentionConnection });
  const { data: chData, isLoading } = useQuery({ queryKey: ["slack-mentions", "channels"], queryFn: BridgeApi.slackMentionChannels });
  const channels = chData?.channels ?? [];
  const connected = !!conn?.connected;

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["slack-mentions"] });
  };

  return (
    <div className="h-full flex flex-col min-h-0">
      <PageHeader
        title="Slack"
        icon={SlackIcon}
        subtitle={connected
          ? `${conn!.bot ? `@${conn!.bot.name} in ${conn!.bot.team}` : "bot token set"} · alfred answers tags in enabled channels`
          : "connect the Alfred bot so alfred can answer @Alfred tags in Slack"}
      />
      <PageBody>
        <ConnectionPanel conn={conn} onSaved={invalidate} />
        <div className="mt-6">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-semibold text-fg">Channels</h2>
            {connected && <AddChannel existing={channels.map((c) => c.channel_id)} onSaved={invalidate} />}
          </div>
          {!connected ? (
            <EmptyState icon={<SlackIcon className="size-8" />} title="Not connected" description="Add the bot token above, then add channels." />
          ) : isLoading ? (
            <p className="text-xs text-fg-subtle">Loading…</p>
          ) : channels.length === 0 ? (
            <EmptyState icon={<SlackIcon className="size-8" />} title="No channels" description="Add a channel Alfred has been invited to, and say who may tag it there." />
          ) : (
            <div className="space-y-3">
              {channels.map((c) => <ChannelEditor key={c.channel_id} channel={c} onSaved={invalidate} />)}
            </div>
          )}
        </div>
      </PageBody>
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs text-fg-muted mb-1">{label}</span>
      {children}
      {hint && <span className="block text-[11px] text-fg-subtle mt-0.5">{hint}</span>}
    </label>
  );
}

function ConnectionPanel({ conn, onSaved }: { conn?: SlackMentionConnection; onSaved: () => void }) {
  const toast = useToast();
  const [botToken, setBotToken] = useState("");
  const [secret, setSecret] = useState("");

  const save = useMutation({
    // Blank = keep the stored value. Neither secret is ever sent to the browser, so
    // an untouched field must not be read as "clear it".
    mutationFn: () => BridgeApi.saveSlackMentionConnection({ bot_token: botToken || undefined, signing_secret: secret || undefined }),
    onSuccess: () => { toast.success("Slack connection saved"); setBotToken(""); setSecret(""); onSaved(); },
    onError: (e) => toast.error("Save failed", String(e)),
  });
  const toggleEnabled = useMutation({
    mutationFn: (enabled: boolean) => BridgeApi.saveSlackMentionConnection({ enabled }),
    onSuccess: () => { toast.success("Updated"); onSaved(); },
    onError: (e) => toast.error("Toggle failed", String(e)),
  });
  const disconnect = useMutation({
    mutationFn: () => BridgeApi.disconnectSlackMentions(),
    onSuccess: () => { toast.success("Secrets forgotten — channels kept"); onSaved(); },
    onError: (e) => toast.error("Disconnect failed", String(e)),
  });

  return (
    <div className="panel p-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-fg">Connection (Alfred bot)</h2>
        {conn?.connected && (
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-1.5 text-xs text-fg-muted" title="Disable to ignore tags without losing this config">
              <input
                type="checkbox" className="size-3.5"
                checked={conn.enabled}
                disabled={toggleEnabled.isPending}
                onChange={(e) => toggleEnabled.mutate(e.target.checked)}
              />
              Enabled
            </label>
            <Pill tone={conn.enabled && conn.has_signing_secret ? "success" : "warning"}>
              {!conn.enabled ? "paused" : conn.has_signing_secret ? "listening" : "needs signing secret"}
            </Pill>
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label={conn?.connected ? "Bot token (set — leave blank to keep)" : "Bot token"} hint="api.slack.com/apps → your app → OAuth & Permissions → Bot User OAuth Token">
          <input className="w-full font-mono text-xs" type="password" value={botToken} onChange={(e) => setBotToken(e.target.value)} placeholder="xoxb-…" autoComplete="off" />
        </Field>
        <Field label={conn?.has_signing_secret ? "Signing secret (set — leave blank to keep)" : "Signing secret"} hint="Basic Information → App Credentials → Signing Secret">
          <input className="w-full font-mono text-xs" type="password" value={secret} onChange={(e) => setSecret(e.target.value)} placeholder="••••••••" autoComplete="off" />
        </Field>
      </div>
      <div className="flex items-center gap-2 mt-3">
        <Button size="sm" onClick={() => save.mutate()} loading={save.isPending} disabled={!botToken && !secret}><Save className="size-3.5" />Save connection</Button>
        {conn?.connected && (
          <Button size="sm" variant="ghost" className="ml-auto" onClick={() => { if (confirm("Forget the bot token and signing secret? Channels are kept.")) disconnect.mutate(); }}>
            <Unplug className="size-3.5" />Disconnect
          </Button>
        )}
      </div>
      {conn && (
        <div className="mt-3 text-[11px] text-fg-subtle space-y-1">
          <p>
            In the Slack app, open <b>Event Subscriptions</b>, turn it on, and set the Request URL to{" "}
            <code className="font-mono text-fg">{conn.request_url}</code> (save the signing secret here first — Slack
            checks the URL straight away). Under <b>Subscribe to bot events</b> add <code>app_mention</code>, plus{" "}
            <code>message.channels</code> and <code>message.groups</code> so a plain "hey alfred" (no @) works too.
          </p>
          <p>Then invite <b>@Alfred</b> into each channel you add below. The secrets stay on the host and are never sent back to this page.</p>
        </div>
      )}
    </div>
  );
}

function AddChannel({ existing, onSaved }: { existing: string[]; onSaved: () => void }) {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const { data, isLoading } = useQuery({ queryKey: ["slack-mentions", "bot-channels"], queryFn: BridgeApi.slackBotChannels, enabled: open });
  const add = useMutation({
    mutationFn: (c: { id: string; name: string }) => BridgeApi.saveSlackMentionChannel({ channel_id: c.id, name: c.name, enabled: true }),
    onSuccess: () => { toast.success("Channel added — now say who may tag Alfred there"); setOpen(false); onSaved(); },
    onError: (e) => toast.error("Add failed", String(e)),
  });
  if (!open) return <Button size="sm" variant="secondary" onClick={() => setOpen(true)}><Plus className="size-3.5" />Add channel</Button>;
  const choices = (data?.channels ?? []).filter((c) => !existing.includes(c.id));
  return (
    <div className="flex items-center gap-2">
      {isLoading ? <span className="text-xs text-fg-subtle">Loading Alfred's channels…</span>
        : data && !data.ok ? <span className="text-xs text-danger">{data.error}</span>
        : choices.length === 0 ? <span className="text-xs text-fg-subtle">No other channels — invite @Alfred to one in Slack first</span>
        : (
          <select className="h-7 text-xs" defaultValue="" onChange={(e) => { const c = choices.find((x) => x.id === e.target.value); if (c) add.mutate(c); }}>
            <option value="" disabled>Pick a channel Alfred is in…</option>
            {choices.map((c) => <option key={c.id} value={c.id}>#{c.name}</option>)}
          </select>
        )}
      <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
    </div>
  );
}

function ChannelEditor({ channel, onSaved }: { channel: SlackMentionChannel; onSaved: () => void }) {
  const toast = useToast();
  const [enabled, setEnabled] = useState(channel.enabled === 1);
  const [trusted, setTrusted] = useState(channel.trusted_users ? (JSON.parse(channel.trusted_users) as string[]).join(", ") : "");

  const save = useMutation({
    mutationFn: () => BridgeApi.saveSlackMentionChannel({
      channel_id: channel.channel_id, enabled,
      trusted_users: trusted.split(",").map((s) => s.trim()).filter(Boolean),
    }),
    onSuccess: () => { toast.success(`#${channel.name ?? channel.channel_id} saved`); onSaved(); },
    onError: (e) => toast.error("Save failed", String(e)),
  });
  const del = useMutation({
    mutationFn: () => BridgeApi.deleteSlackMentionChannel(channel.channel_id),
    onSuccess: () => { toast.success("Channel removed"); onSaved(); },
    onError: (e) => toast.error("Delete failed", String(e)),
  });
  const nobody = !trusted.trim();

  return (
    <div className={cn("panel p-4", !enabled && "opacity-70")}>
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-fg">#{channel.name ?? channel.channel_id}</span>
        <span className="font-mono text-[11px] text-fg-subtle">{channel.channel_id}</span>
        {nobody && <Pill tone="warning">nobody can tag yet</Pill>}
      </div>
      <label className="flex items-center gap-2 mt-2 text-xs text-fg-muted">
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} className="size-3.5" /> enabled (alfred answers @Alfred here)
      </label>
      <div className="mt-3">
        <Field label="Who may tag Alfred" hint="comma-separated Slack names or user ids (U…); * = anyone in the channel; empty = nobody">
          <input className="w-full font-mono text-xs" value={trusted} onChange={(e) => setTrusted(e.target.value)} placeholder="Harry S, Catharina Novita  (or *)" />
        </Field>
      </div>
      <div className="flex items-center gap-2 mt-4">
        <Button size="sm" onClick={() => save.mutate()} loading={save.isPending}><Save className="size-3.5" />Save</Button>
        <Button size="sm" variant="ghost" className="ml-auto" onClick={() => { if (confirm(`Remove #${channel.name ?? channel.channel_id}?`)) del.mutate(); }}><Trash2 className="size-3.5" />Remove</Button>
      </div>
    </div>
  );
}
